import React from 'react';
import { Document, View, Text, StyleSheet } from '@react-pdf/renderer';
import {
  OfficialPage, DetailsBox, Signatures, OFFICIAL_BODY as BODY,
  pdfColors, pdfFontFamily, pdfSpacing, type SchoolBranding,
} from '../../../utils/pdf';
import type { LetterPrint } from '../../../services/letterService';
import { letterBlocks, type LetterBlock } from './letterBlocks';

interface Props {
  letter: LetterPrint;
  branding: SchoolBranding;
  generatedAt: string;
}

const styles = StyleSheet.create({
  subject: { fontFamily: pdfFontFamily.serifBold, fontSize: BODY + 0.5, marginBottom: 16, lineHeight: 1.4 },
  subjectLabel: { fontFamily: pdfFontFamily.serif },
  salutation: { fontFamily: pdfFontFamily.serif, fontSize: BODY, marginBottom: 12 },
  paragraph: { fontFamily: pdfFontFamily.serif, fontSize: BODY, lineHeight: 1.55, textAlign: 'justify', marginBottom: 10, color: '#1f2937' },
  signoff: { marginTop: 14 },
  closing: { fontFamily: pdfFontFamily.serif, fontSize: BODY, marginBottom: 10 },
  signer: { fontFamily: pdfFontFamily.serifBold, fontSize: BODY },
  signerMore: { fontFamily: pdfFontFamily.serif, fontSize: BODY - 0.5, color: pdfColors.textSecondary, marginTop: 1 },
  signatures: { marginTop: pdfSpacing.xxl },
});

const Block: React.FC<{ block: LetterBlock }> = ({ block }) => {
  switch (block.kind) {
    case 'subject':
      return <Text style={styles.subject}><Text style={styles.subjectLabel}>Subject: </Text>{block.text}</Text>;
    case 'salutation':
      return <Text style={styles.salutation}>{block.text}</Text>;
    case 'details':
      return <DetailsBox rows={block.rows} />;
    case 'signoff':
      return (
        <View style={styles.signoff} wrap={false}>
          <Text style={styles.closing}>{block.closing}</Text>
          {block.lines.map((l, i) => <Text key={l} style={i ? styles.signerMore : styles.signer}>{l}</Text>)}
        </View>
      );
    default:
      return <Text style={styles.paragraph}>{block.text}</Text>;
  }
};

/**
 * A letter to a student's family, drawn in the browser on the school's
 * official page (letterhead, reference and date), then the
 * letter as the school wrote it — set in a classic serif, with its subject,
 * salutation, details and sign-off each given their proper form — and lines
 * for both signatures.
 */
const LetterDocument: React.FC<Props> = ({ letter, branding, generatedAt }) => (
  <Document title={`${letter.title} — ${letter.student.name}`} author={branding.name}>
    <OfficialPage
      branding={branding}
      title={letter.title}
      details={[{ label: 'Student', value: letter.student.name }, ...letter.student.details]}
      reference={{ label: 'Ref.', value: letter.ref }}
      date={letter.issuedOn}
      watermark={letter.cancelled ? 'CANCELLED' : undefined}
      generatedAt={generatedAt}
    >
      {letterBlocks(letter.content).map((b, i) => <Block key={i} block={b} />)}
      <View style={styles.signatures}>
        <Signatures slots={['Parent / Guardian', 'Authorised signatory']} />
      </View>
    </OfficialPage>
  </Document>
);

export default LetterDocument;
